import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

// Alert icon component
function AlertIcon({ type, className = "w-5 h-5" }) {
  const icons = {
    lead_quiet: (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    high_value_quiet: (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    email_opened: (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
      </svg>
    ),
    follow_up_due: (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };
  
  return icons[type] || icons.lead_quiet;
}

// Priority badge component
function PriorityBadge({ priority }) {
  const styles = {
    high: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    low: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
  };
  
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${styles[priority] || styles.medium}`}>
      {priority?.toUpperCase()}
    </span>
  );
}

// Format currency
const formatCurrency = (value) => {
  if (!value) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

// Single Alert Item
function AlertItem({ alert, onMarkRead, onAction }) {
  const typeStyles = {
    lead_quiet: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-600 dark:text-amber-400' },
    high_value_quiet: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: 'text-red-600 dark:text-red-400' },
    email_opened: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: 'text-green-600 dark:text-green-400' },
    follow_up_due: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-600 dark:text-blue-400' }
  };
  
  const style = typeStyles[alert.type] || typeStyles.lead_quiet;
  
  return (
    <div className={`p-3 rounded-lg border ${style.bg} ${style.border} ${alert.read ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 ${style.icon}`}>
          <AlertIcon type={alert.type} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link 
              to={`/leads/${alert.lead_id}`}
              className="font-medium text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate"
            >
              {alert.lead_name}
            </Link>
            <PriorityBadge priority={alert.priority} />
          </div>
          
          {alert.organization && (
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{alert.organization}</p>
          )}
          
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{alert.message}</p>
          
          {alert.deal_value > 0 && (
            <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-1">
              {formatCurrency(alert.deal_value)}
            </p>
          )}
          
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
            </span>
            
            <div className="flex items-center gap-2">
              {!alert.read && (
                <button
                  onClick={() => onMarkRead(alert.id)}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  Dismiss
                </button>
              )}
              {!alert.actioned && (
                <Link
                  to={`/leads/${alert.lead_id}`}
                  onClick={() => onAction(alert.id)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  Follow Up →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Alerts Panel Component
export default function AlertsPanel({ collapsed = false }) {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total_unread: 0, high_priority: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, statsRes] = await Promise.all([
        axios.get(`${API}/alerts?limit=10`),
        axios.get(`${API}/alerts/stats`)
      ]);
      setAlerts(alertsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkQuietLeads = useCallback(async () => {
    try {
      await axios.post(`${API}/alerts/check-quiet-leads`);
      fetchAlerts();
    } catch (error) {
      console.error('Error checking quiet leads:', error);
    }
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAlerts();
    // Check for quiet leads on mount
    checkQuietLeads();
    // Set up interval to check every 5 minutes
    const interval = setInterval(checkQuietLeads, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts, checkQuietLeads]);

  const handleMarkRead = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/read`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
      setStats(prev => ({ ...prev, total_unread: Math.max(0, prev.total_unread - 1) }));
    } catch (error) {
      console.error('Error marking alert read:', error);
    }
  };

  const handleAction = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/action`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, actioned: true } : a));
    } catch (error) {
      console.error('Error actioning alert:', error);
    }
  };

  const handleDismissAll = async () => {
    try {
      await axios.post(`${API}/alerts/dismiss-all`);
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      setStats(prev => ({ ...prev, total_unread: 0, high_priority: 0 }));
    } catch (error) {
      console.error('Error dismissing alerts:', error);
    }
  };

  if (collapsed) {
    return (
      <button
        onClick={() => setExpanded(!expanded)}
        className="relative p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
        title="Alerts"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {stats.total_unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {stats.total_unread > 9 ? '9+' : stats.total_unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <h3 className="font-semibold text-slate-900 dark:text-white">Alerts</h3>
            {stats.total_unread > 0 && (
              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium rounded-full">
                {stats.total_unread}
              </span>
            )}
          </div>
          
          {stats.total_unread > 0 && (
            <button
              onClick={handleDismissAll}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              Dismiss All
            </button>
          )}
        </div>
        
        {stats.high_priority > 0 && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            {stats.high_priority} high priority alert{stats.high_priority > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Alerts List */}
      <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto bg-white dark:bg-slate-800">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-slate-200 dark:border-slate-600 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-slate-500 dark:text-slate-400">All caught up!</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">No pending alerts</p>
          </div>
        ) : (
          alerts.map(alert => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onMarkRead={handleMarkRead}
              onAction={handleAction}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {alerts.length > 0 && (
        <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <button
            onClick={checkQuietLeads}
            className="w-full text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
          >
            Check for Quiet Leads
          </button>
        </div>
      )}
    </div>
  );
}
